import { Base, createApp, importWhenVisible } from '@studiometa/js-toolkit';
import { Figure, Tabs, Accordion } from '@studiometa/ui';
import { isDev } from './config.js';
import Menu from './molecules/Menu/index.js';
import ModalSlider from './molecules/ModalSlider/index.js';
import SliderWithBreakpoint from './molecules/SliderWithBreakpoint.js';
import TabsBreakpoints from './molecules/TabsBreakpoints.js';
import MapBasic from './molecules/MapBasic.js';
import SliderTwoCols from './molecules/SliderTwoCols.js';
import ScrollRevealParent from './atoms/ScrollRevealParent.js';
import Modal from './molecules/Modal.js';
import TeamSeeMoreProfiles from './atoms/TeamSeeMoreProfiles.js';
import ModalTrigger from './molecules/ModalTrigger.js';
import Tab from './atoms/Tab.js';
import BannerSlidingMessages from './molecules/BannerSlidingMessages.js';

/**
 * Main App class.
 */
class App extends Base {
  /**
   * App config.
   *
   * @returns {Object}
   */
  static config = {
    log: isDev(),
    name: 'App',
    components: {
      Figure,
      Menu,
      ModalSlider,
      SliderWithBreakpoint,
      TabsBreakpoints,
      MapBasic,
      SliderTwoCols,
      TeamSeeMoreProfiles,
      ScrollRevealParent,
      Modal,
      ModalTrigger,
      Tab,
      BannerSlidingMessages,
      Tabs,
      Accordion,
      FooterLinks: (app) =>
        importWhenVisible(() => import('./molecules/FooterLinks/index.js'), 'FooterLinks', app),
      FieldGroup: (app) =>
        importWhenVisible(() => import('./molecules/FieldGroup.js'), 'FieldGroup', app),
      Marquee: (app) => importWhenVisible(() => import('./molecules/Marquee.js'), 'Marquee', app),
      PageArchivePost: (app) =>
        importWhenVisible(() => import('./organisms/PageArchivePost.js'), 'PageArchivePost', app),
      PageBuilderSectionServices: (app) =>
        importWhenVisible(
          () => import('./molecules/PageBuilder/SectionServices/index.js'),
          'PageBuilderSectionServices',
          app
        ),
    },
  };

  isScroll = false;

  isScrollBottom = false;

  isScrollTop = false;

  /** @inheritdoc */
  mounted() {
    // eslint-disable-next-line dot-notation
    window['console'].log(
      '%c Made with ❤ by Studio Meta %c https://www.studiometa.fr',
      'display: inline-block; background-color: #E30613; color: #ffffff; font-weight: 500; margin-top: 8px; margin-bottom: 8px; padding: 8px; text-decoration: none !important; border: 2px solid #E30613;',
      'display: inline-block; background-color: #ffff; color: #000000; font-weight: 500; margin-top: 8px; margin-bottom: 8px; padding: 8px; text-decoration: none !important; border: 2px solid #E30613;'
    );

    if (window.scrollY) {
      this.setIsScroll(true);
    }
  }

  /**
   * Set is scroll
   *
   * @param {boolean} isScroll Is scroll.
   */
  setIsScroll(isScroll = false) {
    this.isScroll = isScroll;
    if (this.isScroll) {
      this.$el.classList.add('is-scroll');
    } else {
      this.$el.classList.remove('is-scroll');
    }
  }

  /**
   * Set the scroll direction
   *
   * @param {string} scrollDirection The scroll direction
   * @returns {boolean}
   */
  setScrollDirection(scrollDirection = '') {
    if (!scrollDirection) {
      return false;
    }

    if (scrollDirection === 'bottom') {
      this.isScrollBottom = true;
      this.isScrollTop = false;
      this.$el.classList.add('is-scroll-bottom');
      this.$el.classList.remove('is-scroll-top');
    } else if (scrollDirection === 'top') {
      this.isScrollTop = true;
      this.isScrollBottom = false;
      this.$el.classList.add('is-scroll-top');
      this.$el.classList.remove('is-scroll-bottom');
    }

    return true;
  }

  /** @inheritdoc */
  scrolled({ y, last }) {
    if (y <= 0) {
      this.setIsScroll(false);
      return;
    }

    if (!this.isScroll) {
      this.setIsScroll(true);
    }

    if (y > last.y && !this.isScrollBottom) {
      this.setScrollDirection('bottom');
    } else if (y < last.y && !this.isScrollTop) {
      this.setScrollDirection('top');
    }
  }

  /**
   * Listening to click on external modal button
   * @param  {Object} target
   * @returns {void}
   */
  onModalTriggerOpenModal(target) {
    const currentModal = this.$children.Modal.find((modal) => modal.$el.id === target);
    if (currentModal) {
      currentModal.open();
    }
  }
}

export default createApp(App, document.body);
